// Mobile Drawer Component - AgentBheem ERP
// Vue-inspired Modern Dark Theme with Indigo/Purple Accent
import { useEffect, useMemo } from 'react'
import { cn } from '@/lib/utils'
import { useMenuStore } from '@/stores/menuStore'
import { useMenus } from '@/hooks/useMenu'
import { MenuIcon } from './MenuIcon'
import { SidebarItem } from './SidebarItem'
import { X, Settings, LogOut } from 'lucide-react'
import { AgentLoader } from '@/components/ui/AgentLoader'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/features/auth/store/authStore'
import type { MenuGroup } from '@/types/menu'

function MobileMenuGroup({ group }: { group: MenuGroup }) {
  const { expandedGroupIds, toggleGroup } = useMenuStore()
  const isExpanded = expandedGroupIds.includes(group.id)

  return (
    <div className="mb-2">
      <button
        onClick={() => toggleGroup(group.id)}
        className={cn(
          'group flex w-full items-center justify-between rounded-lg px-3 py-2',
          'text-[10px] font-bold uppercase tracking-wider',
          'text-slate-500 transition-all duration-200',
          'hover:bg-indigo-500/10 hover:text-slate-400',
          isExpanded && 'text-slate-400 bg-indigo-500/5'
        )}
      >
        <div className="flex items-center gap-2">
          <div
            className={cn(
              'flex h-6 w-6 items-center justify-center rounded-md transition-all duration-200',
              'bg-slate-800/50',
              'group-hover:bg-indigo-500/20',
              isExpanded && 'bg-indigo-500/20'
            )}
          >
            <MenuIcon
              name={group.name}
              className={cn(
                'h-3.5 w-3.5 transition-colors',
                'text-slate-500 group-hover:text-indigo-400',
                isExpanded && 'text-indigo-400'
              )}
            />
          </div>
          <span>{group.name}</span>
        </div>
        <svg
          className={cn(
            'h-3.5 w-3.5 transition-transform duration-200',
            isExpanded && 'rotate-180'
          )}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isExpanded && group.items.length > 0 && (
        <ul className="mt-1 ml-4 space-y-0.5 border-l-2 border-indigo-500/20 pl-3">
          {group.items.map((item) => (
            <li key={item.id}>
              <SidebarItem item={item} isCollapsed={false} />
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export function MobileDrawer() {
  const navigate = useNavigate()
  const { isMobileOpen, setMobileOpen } = useMenuStore()
  const { logout } = useAuthStore()
  const { data: menus, isLoading } = useMenus()

  // Filter out Project Management group - it's now in the header PM popup
  const filteredMenus = useMemo(() => {
    if (!menus) return menus
    return {
      grouped: menus.grouped?.filter(
        (group) => group.name.toLowerCase() !== 'project management'
      ) || [],
      ungrouped: menus.ungrouped || [],
    }
  }, [menus])

  const handleLogout = () => {
    logout()
    navigate('/auth/login')
    setMobileOpen(false)
  }

  const handleSettings = () => {
    navigate('/settings')
    setMobileOpen(false)
  }

  // Lock body scroll when drawer is open
  useEffect(() => {
    if (isMobileOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isMobileOpen])

  // Close on escape
  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setMobileOpen(false)
      }
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [setMobileOpen])

  if (!isMobileOpen) return null

  return (
    <div className="lg:hidden">
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm animate-fade-in"
        onClick={() => setMobileOpen(false)}
      />

      {/* Drawer */}
      <aside
        className={cn(
          'fixed left-0 top-0 bottom-0 z-50 w-[280px]',
          'bg-gradient-to-b from-[#0f172a] to-[#0c1222]',
          'border-r border-white/[0.08]',
          'flex flex-col',
          'animate-slide-in-left',
          'shadow-[8px_0_40px_rgba(0,0,0,0.5)]'
        )}
      >
        {/* Header */}
        <div className="flex min-h-[70px] items-center justify-between px-4 border-b border-white/[0.08]">
          <div className="flex items-center gap-3">
            <div
              className={cn(
                'flex h-10 w-10 items-center justify-center rounded-xl p-1.5',
                'bg-gradient-to-br from-indigo-500 to-purple-600',
                'shadow-lg shadow-indigo-500/40'
              )}
            >
              <img
                src="/logo.png"
                alt="AgentBheem"
                className="h-full w-full object-contain"
              />
            </div>
            <div className="flex flex-col">
              <span className="text-lg font-bold bg-gradient-to-r from-white to-slate-300 bg-clip-text text-transparent">
                AgentBheem
              </span>
              <span className="text-[9px] text-slate-500 uppercase tracking-widest font-medium">
                Enterprise Resource Planning
              </span>
            </div>
          </div>

          <button
            onClick={() => setMobileOpen(false)}
            className={cn(
              'flex h-8 w-8 items-center justify-center rounded-lg',
              'bg-slate-800/80 border border-white/[0.08]',
              'text-slate-400 transition-all duration-200',
              'hover:bg-indigo-500/20 hover:text-indigo-400 hover:border-indigo-500/30'
            )}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto px-2.5 py-4 scrollbar-thin">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <AgentLoader size="sm" />
            </div>
          ) : filteredMenus?.grouped && filteredMenus.grouped.length > 0 ? (
            <div className="space-y-1">
              {filteredMenus.grouped
                .sort((a, b) => a.position - b.position)
                .map((group) => (
                  <MobileMenuGroup key={group.id} group={group} />
                ))}

              {filteredMenus.ungrouped && filteredMenus.ungrouped.length > 0 && (
                <div className="mt-6 pt-4 border-t border-white/[0.08]">
                  <div className="flex items-center gap-2 px-3 mb-3">
                    <div className="h-px flex-1 bg-gradient-to-r from-indigo-500/20 to-transparent" />
                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                      More
                    </p>
                    <div className="h-px flex-1 bg-gradient-to-l from-indigo-500/20 to-transparent" />
                  </div>
                  <ul className="space-y-0.5">
                    {filteredMenus.ungrouped.map((item) => (
                      <li key={item.id}>
                        <SidebarItem item={item} isCollapsed={false} />
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="w-12 h-12 rounded-xl bg-indigo-500/10 flex items-center justify-center mb-3">
                <svg className="h-6 w-6 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <p className="text-sm text-slate-300">No menus available</p>
              <p className="text-xs text-slate-500 mt-1">Check your permissions</p>
            </div>
          )}
        </nav>

        {/* Bottom Section */}
        <div className="border-t border-white/[0.08] p-2.5 bg-gradient-to-t from-black/20 to-transparent">
          <div className="space-y-1">
            {/* Settings */}
            <button
              onClick={handleSettings}
              className={cn(
                'group flex w-full items-center gap-3 rounded-xl px-3 py-2.5',
                'text-slate-400 transition-all duration-200',
                'hover:bg-indigo-500/10 hover:text-slate-200'
              )}
            >
              <div
                className={cn(
                  'flex h-8 w-8 items-center justify-center rounded-lg transition-all duration-200',
                  'bg-slate-800/50',
                  'group-hover:bg-indigo-500/20'
                )}
              >
                <Settings className="h-4 w-4" />
              </div>
              <span className="text-sm font-medium">Settings</span>
            </button>

            {/* Logout */}
            <button
              onClick={handleLogout}
              className={cn(
                'group flex w-full items-center gap-3 rounded-xl px-3 py-2.5',
                'text-red-400 transition-all duration-200',
                'hover:bg-red-500/10'
              )}
            >
              <div
                className={cn(
                  'flex h-8 w-8 items-center justify-center rounded-lg transition-all duration-200',
                  'bg-slate-800/50',
                  'group-hover:bg-red-500/20'
                )}
              >
                <LogOut className="h-4 w-4" />
              </div>
              <span className="text-sm font-medium">Logout</span>
            </button>
          </div>
        </div>
      </aside>
    </div>
  )
}
